"use client";

// ============================================================
// CLUBHUB – Dokumente: das Blatt zu einer Datei
// ============================================================
// Faehrt von rechts herein und laesst den Ordner stehen (`?datei=`
// in der Adresse — der Zurueck-Knopf schliesst es). Die Pfeile oben
// und die Pfeiltasten blaettern durch die gezeigten Dateien: wer
// dreissig Fotos vom Sommerfest durchsieht, will nicht dreissigmal
// schliessen und oeffnen.
//
// Von oben nach unten: die Sache selbst, was man damit tut, was man
// daran aendern kann (Name, Beschreibung), was sie mitbringt, ihre
// Fassungen, ihre Links, und ob sie auf die Website darf.
//
// Wer nur lesen darf, sieht die Datei, ihre Angaben und Fassungen —
// ohne Knoepfe zum Aendern und ohne die Teilen-Links.
// ============================================================

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Copy, Download, FolderInput, Globe, History, Link2, Trash2, Upload } from "lucide-react";
import { Seitenblatt } from "@/components/ui/sheet";
import { Button, buttonVariants, confirmDialog, Input, Textarea, toast } from "@/components/ui";
import { ZeichenKnopf } from "@/components/ui/zeichen-knopf";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { cn, formatDate, formatDateTime } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import {
  DATEIARTEN,
  dateiArt,
  endung,
  endungsLabel,
  formatBytes,
  grundname,
  jetztIso,
  linkStatus,
  namePruefen,
  pfadText,
  sehenDuerfen,
  slug,
  speicherort,
  wirksameSicht,
} from "@/lib/dokumente/format";
import type { DocumentView } from "@/lib/dokumente/views";
import { mitDatei } from "./ablage-zustand";
import { useAblage } from "./ablage-kontext";
import { inZwischenablage, SICHT_ZEICHEN } from "./dialoge";
import { Grossvorschau } from "./vorschau";
import { dateiAendern, fassungWiederherstellen, linkWiderrufen } from "@/app/(app)/dokumente/actions";
import { errorOf } from "@/lib/action-result";

function Abschnitt({ titel, aktion, children }: { titel: string; aktion?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-border-subtle space-y-3 border-t pt-5">
      <div className="flex min-h-7 items-center justify-between gap-3">
        <h3 className="text-overline text-text-tertiary">{titel}</h3>
        {aktion}
      </div>
      {children}
    </section>
  );
}

function Angabe({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-tertiary">{titel}</dt>
      <dd className="text-text-secondary min-w-0">{children}</dd>
    </>
  );
}

/** Steht gerade ein Dialog (oder eine Rueckfrage) ueber dem Blatt? */
function dialogOffen(): boolean {
  return document.querySelector("[data-dialog]") !== null;
}

export function DateiBlatt({
  datei,
  offen,
  onSchliessen,
  vorher,
  nachher,
}: {
  datei: DocumentView;
  /** Zu bleibt das Blatt eingehaengt, damit es hinausfahren kann. */
  offen: boolean;
  onSchliessen: () => void;
  vorher: string | null;
  nachher: string | null;
}) {
  const { bestand, daten, aendern, setzen, oeffnen, herunterladen, dateienLoeschen, neueVersion, zumOrdner } = useAblage();
  const router = useRouter();
  const echt = daten.quelle === "datenbank";
  const schreiben = daten.darfSchreiben;
  const [ansicht, setAnsicht] = useState<number | null>(null);
  const [basis, setBasis] = useState(grundname(datei.name));
  const [beschreibung, setBeschreibung] = useState(datei.description ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const versionFeld = useRef<HTMLInputElement>(null);

  // Eine andere Datei (Blaettern, Umbenennen aus dem Menue): die
  // Felder fangen bei ihr neu an.
  const [stand, setStand] = useState({ id: datei.id, name: datei.name, beschreibung: datei.description });
  const [warOffen, setWarOffen] = useState(offen);
  const neuGeoeffnet = offen && !warOffen;
  if (offen !== warOffen) setWarOffen(offen);
  if (neuGeoeffnet || stand.id !== datei.id || stand.name !== datei.name || stand.beschreibung !== datei.description) {
    setStand({ id: datei.id, name: datei.name, beschreibung: datei.description });
    setBasis(grundname(datei.name));
    setBeschreibung(datei.description ?? "");
    setFehler(null);
    if (neuGeoeffnet || stand.id !== datei.id) setAnsicht(null);
  }

  // Pfeiltasten blaettern — nicht in einem Feld, nicht mit Zusatztaste,
  // nicht in einer Auswahlliste und nie hinter einem offenen Dialog:
  // dort wuerde die Rueckfrage „Löschen?“ sonst nach dem Blaettern
  // die ANDERE Datei treffen.
  useEffect(() => {
    if (!offen) return undefined;
    function taste(e: KeyboardEvent) {
      if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return;
      if (el instanceof HTMLElement && (el.isContentEditable || el.closest("[role=listbox], [role=menu], [role=radiogroup], [data-radix-popper-content-wrapper]"))) return;
      if (dialogOffen()) return;
      if (e.key === "ArrowLeft" && vorher) setzen({ datei: vorher });
      if (e.key === "ArrowRight" && nachher) setzen({ datei: nachher });
    }
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [offen, vorher, nachher, setzen]);

  /** Escape im Feld: Entwurf verwerfen und das Feld verlassen — das naechste Escape schliesst das Blatt. */
  function feldEscape(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (e.key !== "Escape") return;
    setBasis(grundname(datei.name));
    setBeschreibung(datei.description ?? "");
    setFehler(null);
    e.currentTarget.blur();
  }

  const art = dateiArt(datei.mimeType, datei.name);
  const e = endung(datei.name);
  const sicht = wirksameSicht(datei.folderId, bestand.folders);
  const aussage = sehenDuerfen(sicht, daten);
  const SichtZeichen = SICHT_ZEICHEN[sicht.visibility];
  const pfad = pfadText(datei.folderId, bestand.folders);
  const aeltere = ansicht !== null ? datei.versions.find((v) => v.version === ansicht) : null;
  const gezeigt: DocumentView = aeltere
    ? {
        ...datei,
        size: aeltere.size,
        mimeType: aeltere.mimeType,
        version: aeltere.version,
        adresse: datei.adresse ? `${datei.adresse}?v=${aeltere.version}` : null,
        width: null,
        height: null,
        pages: null,
      }
    : datei;
  const erste = datei.versions.at(-1);
  const gesamt = datei.versions.reduce((s, v) => s + v.size, 0);
  const geaendert = grundname(datei.name) !== basis.trim() || (datei.description ?? "") !== beschreibung.trim();
  const oeffentlicheAdresse = daten.websiteBase ? `${daten.websiteBase}/dokumente/${slug(datei.name)}` : null;

  async function speichern() {
    const name = e ? `${basis.trim()}.${e}` : basis.trim();
    const geschwister = bestand.documents.filter((d) => d.folderId === datei.folderId && d.id !== datei.id).map((d) => d.name);
    const f = basis.trim() ? namePruefen(name, geschwister, "Datei") : "Bitte einen Namen eingeben.";
    if (f) {
      setFehler(f);
      return;
    }
    const fehler = errorOf(await dateiAendern(datei.id, { name, description: beschreibung.trim() || null }));
    if (fehler) {
      setFehler(fehler);
      return;
    }
    aendern((b) => mitDatei(b, datei.id, (d) => ({ ...d, name, description: beschreibung.trim() || null, updatedAt: d.updatedAt })));
    if (echt) router.refresh();
    toast.success("Gespeichert");
  }

  async function wiederherstellen(version: number) {
    const v = datei.versions.find((x) => x.version === version);
    if (!v) return;
    const ok = await confirmDialog({
      title: `Version ${version} wiederherstellen?`,
      description: `Sie wird als Version ${datei.version + 1} die aktuelle. Die jetzige Version ${datei.version} bleibt in der Liste.`,
      confirmLabel: "Wiederherstellen",
    });
    if (!ok) return;
    const r = await fassungWiederherstellen(datei.id, version);
    const fehler = errorOf(r);
    if (fehler) {
      toast.error(fehler);
      return;
    }
    const jetzt = jetztIso();
    if (echt) {
      setAnsicht(null);
      router.refresh();
      toast.success(`Version ${version} ist wieder die aktuelle`);
      return;
    }
    aendern((b) =>
      mitDatei(b, datei.id, (d) => {
        const neu = d.version + 1;
        const storageKey = v.storageKey.replace(/\/v\d+\//, `/v${neu}/`);
        return {
          ...d,
          version: neu,
          size: v.size,
          mimeType: v.mimeType,
          storageKey,
          updatedAt: jetzt,
          uploadedBy: daten.me,
          versions: [{ ...v, version: neu, storageKey, createdAt: jetzt, uploadedBy: daten.me, note: `Wiederhergestellt aus Version ${version}` }, ...d.versions],
        };
      }),
    );
    setAnsicht(null);
    toast.success(`Version ${version} ist wieder die aktuelle`);
  }

  async function widerrufen(linkId: string) {
    const fehler = errorOf(await linkWiderrufen(linkId));
    if (fehler) {
      toast.error(fehler);
      return;
    }
    if (echt) router.refresh();
    aendern((b) => mitDatei(b, datei.id, (d) => ({ ...d, shares: d.shares.map((l) => (l.id === linkId ? { ...l, revoked: true } : l)) })));
    toast.success("Link widerrufen", { description: "Wer ihn noch hat, sieht ab jetzt eine Absage." });
  }

  return (
    <Seitenblatt
      offen={offen}
      titel={datei.name}
      // Ein Klick in einen Dialog ueber dem Blatt (etwa in die Liste von
      // „Gültig“ beim Teilen, die am Rumpf haengt) gilt fuer Radix als
      // „daneben“ — das Blatt bleibt in diesem Fall offen.
      onSchliessen={() => {
        if (dialogOffen()) return;
        onSchliessen();
      }}
      breite="560px"
      kopfZusatz={
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-body-md text-text-primary truncate font-medium">{datei.name}</p>
            <button
              type="button"
              onClick={() => datei.folderId && zumOrdner(datei.folderId)}
              className="text-caption text-text-tertiary hover:text-text-primary block max-w-full truncate text-left transition-colors"
              title="Zum Ordner"
            >
              {pfad}
            </button>
          </div>
          <div className="-my-0.5 flex shrink-0 items-center">
            <ZeichenKnopf label="Vorherige Datei" groesse="mittel" disabled={!vorher} onClick={() => vorher && setzen({ datei: vorher })}>
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </ZeichenKnopf>
            <ZeichenKnopf label="Nächste Datei" groesse="mittel" disabled={!nachher} onClick={() => nachher && setzen({ datei: nachher })}>
              <ChevronRight className="h-4 w-4" aria-hidden />
            </ZeichenKnopf>
          </div>
        </div>
      }
    >
      <div className="space-y-5 pb-4">
        {aeltere && (
          <div className="border-review/25 bg-review-subtle flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2">
            <History className="text-review h-4 w-4 shrink-0" aria-hidden />
            <p className="text-body-sm text-text-primary min-w-0 flex-1">
              Du siehst Version {aeltere.version} vom {formatDate(aeltere.createdAt)}.
            </p>
            <Button size="sm" variant="ghost" onClick={() => setAnsicht(null)}>
              Zur aktuellen
            </Button>
            {schreiben && (
              <Button size="sm" variant="secondary" onClick={() => void wiederherstellen(aeltere.version)}>
                Wiederherstellen
              </Button>
            )}
          </div>
        )}

        <div className="bg-bg-secondary grid min-h-[200px] place-items-center overflow-hidden rounded-xl">
          <Grossvorschau datei={gezeigt} clubName={daten.clubName} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => herunterladen([datei.id])}>
            <Download className="h-4 w-4" aria-hidden />
            Herunterladen
          </Button>
          {schreiben && (
            <>
              <Button variant="secondary" onClick={() => oeffnen({ art: "teilen", dateiId: datei.id })}>
                <Link2 className="h-4 w-4" aria-hidden />
                Link teilen
              </Button>
              <Button variant="secondary" onClick={() => oeffnen({ art: "dateien-verschieben", dateiIds: [datei.id] })}>
                <FolderInput className="h-4 w-4" aria-hidden />
                Verschieben
              </Button>
              <Button variant="ghost" className="text-danger hover:text-danger" onClick={() => void dateienLoeschen([datei.id])}>
                <Trash2 className="h-4 w-4" aria-hidden />
                Löschen
              </Button>
            </>
          )}
        </div>

        {!schreiben && datei.description && <p className="text-body-sm text-text-secondary whitespace-pre-line">{datei.description}</p>}

        {schreiben && (
        <form
          className="space-y-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            void speichern();
          }}
        >
          <div className="flex items-end gap-2">
            <Input
              label="Name"
              value={basis}
              onChange={(ev) => {
                setBasis(ev.target.value);
                setFehler(null);
              }}
              onKeyDown={feldEscape}
              error={fehler ?? undefined}
              wrapperClassName="min-w-0 flex-1"
              autoComplete="off"
            />
            {e && <span className={cn("text-body-sm text-text-tertiary pb-2.5", fehler && "pb-8")}>.{e}</span>}
          </div>
          <Textarea
            label="Beschreibung"
            value={beschreibung}
            onKeyDown={feldEscape}
            onChange={(ev) => setBeschreibung(ev.target.value)}
            placeholder="Wofür ist die Datei, was ist zu beachten?"
            rows={3}
          />
          {geaendert && (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setBasis(grundname(datei.name));
                  setBeschreibung(datei.description ?? "");
                  setFehler(null);
                }}
              >
                Verwerfen
              </Button>
              <Button type="submit">Speichern</Button>
            </div>
          )}
        </form>
        )}

        <Abschnitt titel="Angaben">
          <dl className="text-caption grid grid-cols-[7.5rem_1fr] gap-x-4 gap-y-2">
            <Angabe titel="Art">
              {DATEIARTEN[art].label} · {endungsLabel(datei.name) || "ohne Endung"}
            </Angabe>
            <Angabe titel="Größe">
              <span className="tabular-nums">
                {formatBytes(datei.size)}
                {datei.versions.length > 1 && ` · alle Versionen ${formatBytes(gesamt)}`}
              </span>
            </Angabe>
            {datei.width && datei.height && (
              <Angabe titel="Maße">
                <span className="tabular-nums">
                  {formatDecimal(datei.width)} × {formatDecimal(datei.height)} Pixel
                </span>
              </Angabe>
            )}
            {datei.pages !== null && (
              <Angabe titel="Seiten">
                <span className="tabular-nums">{formatDecimal(datei.pages)}</span>
              </Angabe>
            )}
            <Angabe titel="Sehen dürfen">
              <span className="flex items-start gap-1.5">
                <SichtZeichen className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{aussage.satz}</span>
              </span>
            </Angabe>
            <Angabe titel="Hochgeladen">
              <span className="tabular-nums">{formatDateTime(erste?.createdAt ?? datei.createdAt)}</span>
              {erste?.uploadedBy && ` von ${erste.uploadedBy.name}`}
            </Angabe>
            {datei.versions.length > 1 && (
              <Angabe titel="Zuletzt geändert">
                <span className="tabular-nums">{formatDateTime(datei.updatedAt)}</span>
                {datei.uploadedBy && ` von ${datei.uploadedBy.name}`}
              </Angabe>
            )}
            <Angabe titel="Speicherort">{speicherort(daten.storage)}</Angabe>
            <Angabe titel="Schlüssel">
              <span className="block truncate font-mono text-[11px]" title={datei.storageKey}>
                {datei.storageKey}
              </span>
            </Angabe>
            <Angabe titel="Prüfsumme">
              {datei.checksum ? (
                <span className="font-mono text-[11px]" title={datei.checksum}>
                  {datei.checksum.slice(0, 16)}…
                </span>
              ) : (
                "wird nach dem Hochladen berechnet"
              )}
            </Angabe>
          </dl>
        </Abschnitt>

        <Abschnitt
          titel={`Versionen (${formatDecimal(datei.versions.length)})`}
          aktion={
            schreiben && (
            <>
              <input
                ref={versionFeld}
                type="file"
                accept={e ? `.${e}` : undefined}
                className="hidden"
                onChange={(ev) => {
                  const f = ev.target.files?.[0];
                  if (f) neueVersion(datei.id, f);
                  ev.target.value = "";
                }}
              />
              <Button size="sm" variant="outline" onClick={() => versionFeld.current?.click()}>
                <Upload className="h-3.5 w-3.5" aria-hidden />
                Neue Version
              </Button>
            </>
            )
          }
        >
          <ul className="space-y-1">
            {datei.versions.map((v, i) => (
              <li
                key={v.version}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-2 py-2",
                  ansicht === v.version ? "bg-review-subtle" : i === 0 && ansicht === null ? "bg-bg-secondary" : "",
                )}
              >
                <span className="text-body-sm text-text-primary w-8 shrink-0 font-medium tabular-nums">v{v.version}</span>
                <span className="min-w-0 flex-1">
                  <span className="text-body-sm text-text-secondary block tabular-nums">
                    {formatDateTime(v.createdAt)} · {formatBytes(v.size)}
                  </span>
                  <span className="text-caption text-text-tertiary block truncate">
                    {[v.uploadedBy?.name, v.note].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {i === 0 ? (
                  <TonBadge ton="neutral">aktuell</TonBadge>
                ) : (
                  <span className="flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setAnsicht(ansicht === v.version ? null : v.version)}>
                      {ansicht === v.version ? "Schließen" : "Ansehen"}
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Abschnitt>

        {schreiben && (
        <Abschnitt
          titel="Teilen-Links"
          aktion={
            <Button size="sm" variant="outline" onClick={() => oeffnen({ art: "teilen", dateiId: datei.id })}>
              <Link2 className="h-3.5 w-3.5" aria-hidden />
              Link erstellen
            </Button>
          }
        >
          {datei.shares.length === 0 ? (
            <p className="text-body-sm text-text-tertiary">
              Noch kein Link. Ein Link mit Ablaufdatum gibt jemandem ohne Zugang — Steuerbüro, Verband, Presse — genau diese Datei.
            </p>
          ) : (
            <ul className="space-y-2">
              {datei.shares.map((l) => {
                const status = linkStatus(l, daten.today);
                return (
                  <li key={l.id} className={cn("border-border rounded-lg border px-3 py-2.5", status !== "aktiv" && "opacity-70")}>
                    <div className="flex items-center gap-2">
                      {l.url ? (
                        <span className="text-caption text-text-secondary min-w-0 flex-1 truncate font-mono" title={l.url}>
                          {l.url.replace("https://", "")}
                        </span>
                      ) : (
                        // Mit Datenbank steht die Adresse nur beim Erstellen da (gespeichert ist ihre Pruefsumme).
                        <span className="text-caption text-text-secondary min-w-0 flex-1 truncate tabular-nums">
                          Link vom {formatDateTime(l.createdAt)}
                        </span>
                      )}
                      {status === "aktiv" && l.url && (
                        <Button size="sm" variant="ghost" onClick={() => void inZwischenablage(l.url!, "Link kopiert")}>
                          <Copy className="h-3.5 w-3.5" aria-hidden />
                          Kopieren
                        </Button>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      {status === "aktiv" ? (
                        <TonBadge ton="success">{l.expiresAt ? `gültig bis ${formatDate(l.expiresAt)}` : "ohne Ablauf"}</TonBadge>
                      ) : status === "abgelaufen" ? (
                        <TonBadge ton="warning">abgelaufen am {formatDate(l.expiresAt)}</TonBadge>
                      ) : (
                        <TonBadge ton="neutral">widerrufen</TonBadge>
                      )}
                      <span className="text-caption text-text-tertiary tabular-nums">
                        {formatDecimal(l.views)} {l.views === 1 ? "Aufruf" : "Aufrufe"}
                        {!l.allowDownload && " · nur ansehen"}
                        {l.createdBy && ` · von ${l.createdBy.name}`}
                      </span>
                      {status === "aktiv" && (
                        <button
                          type="button"
                          onClick={() => void widerrufen(l.id)}
                          className="text-caption text-text-tertiary hover:text-danger ml-auto transition-colors"
                        >
                          Widerrufen
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Abschnitt>
        )}

        <Abschnitt titel="Auf der Website">
          {sicht.visibility === "PUBLIC" ? (
            <div className="space-y-3">
              <p className="text-body-sm text-text-secondary">
                {oeffentlicheAdresse
                  ? "Die Datei liegt öffentlich und hat eine feste Adresse. Sie bleibt gleich, wenn du später eine neue Version hochlädst."
                  : "Die Datei liegt öffentlich. Eine feste Adresse auf der Vereinsseite bekommt sie, sobald die Website steht."}
              </p>
              {oeffentlicheAdresse && (
              <div className="border-border bg-bg-primary flex items-center gap-2 rounded-lg border py-1.5 pr-1.5 pl-3">
                <Globe className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="text-caption text-text-secondary min-w-0 flex-1 truncate font-mono" title={oeffentlicheAdresse}>
                  {oeffentlicheAdresse.replace("https://", "")}
                </span>
                <Button size="sm" variant="ghost" onClick={() => void inZwischenablage(oeffentlicheAdresse, "Adresse kopiert")}>
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                  Kopieren
                </Button>
              </div>
              )}
              {/* BACKEND: auf der Website als Download-Liste oder Bild einbinden — Modul Website. */}
              <Link href="/website" className={buttonVariants({ variant: "secondary", size: "sm" })}>
                {art === "bild" ? "In eine Seite einsetzen" : "Als Download auf eine Seite"}
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-body-sm text-text-secondary">
                Auf die Website kommen nur Dateien aus öffentlichen Ordnern. Diese hier sehen: {aussage.kurz}.
              </p>
              {schreiben && (
                <Button size="sm" variant="secondary" onClick={() => oeffnen({ art: "dateien-verschieben", dateiIds: [datei.id] })}>
                  <FolderInput className="h-3.5 w-3.5" aria-hidden />
                  In einen öffentlichen Ordner …
                </Button>
              )}
            </div>
          )}
        </Abschnitt>
      </div>
    </Seitenblatt>
  );
}
